<template>
    <div class="fst-LlmsEditor">
        <el-main>
            <el-form ref="form" :model="model" label-position="top" :show-message="false">
                <el-row :gutter="20">
                    <el-col :span="10">
                        <el-form-item prop="name" :label="$t('llms.editor.name')" required>
                            <el-input v-model="model.name" clearable />
                        </el-form-item>
                    </el-col>
                    <el-col :span="10">
                        <el-form-item prop="alias" :label="$t('llms.editor.alias')" required>
                            <el-input v-model="model.alias" clearable />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item prop="contextSize" :label="$t('llms.editor.contextSize')">
                            <el-input-number v-model="model.contextSize" :min="1" controls-position="right" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item prop="description" :label="$t('llms.editor.description')">
                    <el-input v-model="model.description" type="textarea" :rows="2" />
                </el-form-item>
                <el-form-item ref="script" class="script" prop="script">
                    <template #label>
                        <el-dropdown @command="changeScriptStub">
                            <span class="el-dropdown-link">
                                <span class="mr-2">{{ $t('llms.editor.script') }}</span>
                                <el-link type="primary" :underline="false"><i class="fas fa-chevron-down"></i></el-link>
                            </span>
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <el-dropdown-item v-for="s in scriptStubs" :key="s.id" :command="s.id">{{ s.name }}</el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>
                    </template>
                    <MonacoEditor class="styled" ref="monacoEditor" :value="model.script" language="iqp_llm_completion_script" @change="v => model.script = v" />
                </el-form-item>
            </el-form>
        </el-main>
        <el-footer>
            <el-button type="primary" :disabled="!hasChangedFields" @click="save()">{{ $t('save') }}</el-button>
            <el-button type="primary" @click="refresh()">{{ $t('cancel') }}</el-button>
            <el-button v-if="model.script" type="primary" plain @click="check()">{{ $t('check') }}</el-button>
        </el-footer>
    </div>
</template>

<script>
    import _ from 'lodash';
    import { mapState, mapActions } from 'vuex';
    import MonacoEditor from '@/commons/components/monaco-editor/MonacoEditor';
    import LlmsCheck from './LlmsCheck.vue';

    const DEFAULT_MODEL = {
        id: null,
        name: '',
        alias: '',
        description: '',
        contextSize: 2048,
        script: ''
    };

    export default {
        name: "LlmsEditor",
        components: {MonacoEditor},
        props: {
            value: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                model: {},
                scriptStub: null
            }
        },
        computed: {
            ...mapState('llm', ['scriptStubs']),
            hasChangedFields() {
                return !_.isEqual(_.assign({}, DEFAULT_MODEL, this.value), this.model);
            }
        },
        mounted() {
            this.refresh();
            this.getScriptStubs({prepare: {loader: false}});
        },
        methods: {
            ...mapActions('llm', ['saveLlm','getScriptStubs','getScriptStubContent']),
            refresh() {
                this.model = _.merge({}, DEFAULT_MODEL, this.value);
                this.$refs.monacoEditor._setValue(this.model.script);
            },
            save() {
                this.$refs.form.validate(valid => {
                    if (valid) {
                        let model = _.merge(_.pick(this.model, _.keys(DEFAULT_MODEL)), {
                            script: this.$refs.monacoEditor._getValue()
                        });
                        //console.log('save', model); return;
                        this.saveLlm({request: model, prepare: {target: this.$el}}).then(data => {
                            this.$message({message: this.$t('llms.editor.msg.success'), type: 'success'});
                            this.model = _.merge({}, DEFAULT_MODEL, data);
                            this.$emit('afterSave', data);
                        });
                    }
                })
            },
            check() {
                this.$modal.dialog(LlmsCheck, {
                    title: this.$t('llms.check.title'),
                    modalParams: {width: 1000, clickToClose: false},
                    ':value': {
                        script: _.pick(this.model, ['script'])
                    },
                    buttons: [{
                        name: this.$t('check'),
                        type: 'primary',
                        handler: ({component}) => {
                            component.apply();
                        }
                    }, 'cancel']
                });
            },
            clear() {
                this.model = _.cloneDeep(DEFAULT_MODEL);
                this.$refs.monacoEditor._setValue('');
            },
            changeScriptStub(id) {
                let script = _.get(this.scriptStubs, `${id}.content`);
                if (script) {
                    this.model.script = script;
                } else {
                    this.getScriptStubContent({request: id, prepare: {target: this.$refs.script.$el}}).then(data => {
                        _.set(this.scriptStubs, `${id}.content`, data);
                        this.model.script = data;
                    });
                }
            }
        }
    }
</script>

<style lang="scss">
    @use "@/assets/imports.scss" as *;

    .fst-LlmsEditor {
        height: 100%;
        max-height: 100%;
        display: flex;
        flex-direction: column;
        .el-main {
            flex: 1;
            padding: 0;
            .el-form {
                height: 100%;
                max-width: 1000px;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                .el-input-number {
                    width: 100%;
                }
                .script {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    margin-bottom: 0 !important;
                    .el-form-item__content {
                        flex: 1;
                        min-height: 200px;
                        display: block;
                    }
                }
            }
        }
        .el-footer {
            height: auto !important;
            padding: 20px 0 0 0;
            border-top: 1px solid $iq-border-color-light;
        }
    }
</style>
